/* ===========================================================================
   Gift Set Builder — client engine v6 (claude.ai/design "v3" port)
   ---------------------------------------------------------------------------
   Vanilla JS, no jQuery. One instance per [data-section-type="gift-set-builder"],
   re-init-guarded. Editorial-luxe, occasion-first, 3 steps: build → note → review.

   SELECTION: this.boxArr is an ordered list of { id, qty }. min/max count TOTAL
   PIECES; maxPerItem caps any one product. Occasion chips pre-select a mood AND
   surface a ready-made kit (real product) in a cross-sell line that opens a
   popover. Dimensional kraft vessel fills with one thumbnail per piece + a wax
   seal once giftable; a serif line rises with the count. Desktop = sticky aside;
   mobile = a bottom sheet opened from a sticky bar.

   Cart: ONE batch POST /cart/add.js {items:[…]} — one line per distinct product
   with its real quantity, sharing a `_bundle_id`; the gift card ONLY in the native
   cart.note (2026-09-14 — order attributes and line properties duplicated it into
   ShipStation's Note From Buyer / item descriptions); redirect to /cart. Idempotent.
   =========================================================================== */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'gsb_v6_';
  var STEPS = ['build', 'note', 'review'];
  var SHEET_BP = '(max-width: 899px)';

  var BOX_LINES = [
    'An empty box, lined and waiting.',
    'One piece in. The evening starts to take shape.',
    'Two pieces. The warm water is already running.',
    'Three — enough for a slow Sunday, start to finish.',
    'Four, and a quiet hour waiting in each.',
    'A whole evening they can sink into.'
  ];
  function boxLine(count) { return BOX_LINES[count] || 'A generous box — the kind they’ll remember.'; }

  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  function parseJSONScript(root, sel) { var el = root.querySelector(sel); if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { return null; } }
  function tokens(str) { return (str || '').toLowerCase().split(/[\s,]+/).map(function (t) { return t.trim(); }).filter(Boolean); }
  function el(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function clear(n) { while (n && n.firstChild) n.removeChild(n.firstChild); }
  // GA4 engagement tracking — mirrors assets/mds-tracking.js delivery (gtag direct
  // with a dataLayer fallback; never throws). Public measurement id (already exposed
  // in page source as gtag/js?id=…). Fills the gap the click layer can't: the box
  // VALUE at add-to-cart success (the AOV signal), step funnel, kit add, errors.
  var GSB_GA4_ID = 'G-04VG181C0C';
  function gsbTrack(event, params) {
    try {
      if (typeof window.gtag === 'function') {
        var p = { send_to: GSB_GA4_ID };
        if (params) for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) p[k] = params[k];
        window.gtag('event', event, p);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (params) for (var j in params) if (Object.prototype.hasOwnProperty.call(params, j)) payload[j] = params[j];
      window.dataLayer.push(payload);
    } catch (e) { /* never break the builder for analytics */ }
  }
  function svg(paths, size, sw) {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('width', size); s.setAttribute('height', size); s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', 'none'); s.setAttribute('stroke', 'currentColor'); s.setAttribute('stroke-width', sw || 1.6);
    s.setAttribute('stroke-linecap', 'round'); s.setAttribute('stroke-linejoin', 'round'); s.setAttribute('aria-hidden', 'true');
    paths.forEach(function (d) { var p = document.createElementNS('http://www.w3.org/2000/svg', 'path'); p.setAttribute('d', d); s.appendChild(p); });
    return s;
  }
  var IC = {
    minus: ['M5 12h14'], plus: ['M12 5v14', 'M5 12h14'], x: ['M18 6 6 18', 'M6 6l12 12'],
    leaf: ['M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z', 'M2 21c0-3 1.85-5.36 5.08-6']
  };
  function makeFormatMoney(fmt) {
    fmt = fmt || '${{amount}}';
    return function (cents) {
      var a = (cents || 0) / 100;
      var c = a.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var nd = Math.round(a).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, nd)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, c)
        .replace(/\{\{\s*amount\s*\}\}/g, c);
    };
  }
  var THUMB_ROT = [-6, 5, -3, 7, -5];

  function Builder(root) {
    this.root = root;
    this.cfg = parseJSONScript(root, '[data-gsb-config]') || {};
    this.products = parseJSONScript(root, '[data-gsb-products]') || [];
    this.fmt = makeFormatMoney(this.cfg.money_format);
    this.min = parseInt(this.cfg.min_items, 10) || 3;
    this.max = parseInt(this.cfg.max_items, 10) || 5;
    this.maxPerItem = parseInt(this.cfg.max_per_item, 10) || 3;
    this.feelings = Array.isArray(this.cfg.feelings) ? this.cfg.feelings : [];
    this.types = Array.isArray(this.cfg.types) ? this.cfg.types : [];
    this.occasions = Array.isArray(this.cfg.occasions) ? this.cfg.occasions : [];
    this.typesById = {}; this.feelingsById = {}; this.occasionsById = {};
    this.types.forEach(function (t) { this.typesById[t.id] = t; }, this);
    this.feelings.forEach(function (f) { this.feelingsById[f.id] = f; }, this);
    this.occasions.forEach(function (o) { this.occasionsById[o.id] = o; o._tags = String(o.tags || '').toLowerCase().split(/[\s,]+/).filter(Boolean); }, this);

    this.byId = {};
    this.products.forEach(function (p) { this.byId[String(p.id)] = p; }, this);
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-gsb-card]'));

    this.boxArr = [];
    this.activeFeeling = null;
    this.activeOccasion = null;
    this.activeType = 'all';
    this.activeKit = null;
    this.gift = null;
    this.step = 'build';
    this.reached = 0;

    this.els = {
      topback: root.querySelector('[data-gsb-topback]'),
      backLabel: root.querySelector('[data-gsb-back-label]'),
      segs: Array.prototype.slice.call(root.querySelectorAll('[data-gsb-seg]')),
      occasionrow: root.querySelector('[data-gsb-occasionrow]'),
      occasionWrap: root.querySelector('[data-gsb-occasion-wrap]'),
      moodrow: root.querySelector('[data-gsb-moodrow]'),
      chiprow: root.querySelector('[data-gsb-chiprow]'),
      kitXsell: root.querySelector('[data-gsb-kit-xsell]'),
      kitName: root.querySelector('[data-gsb-kit-name]'),
      kitView: root.querySelector('[data-gsb-kit-view]'),
      announce: root.querySelector('[data-gsb-announce]'),
      status: root.querySelector('[data-gsb-status]'),
      grid: root.querySelector('[data-gsb-grid]'),
      zero: root.querySelector('[data-gsb-zero]'),
      zeroRelax: root.querySelector('[data-gsb-zero-relax]'),
      vesselEmpty: root.querySelector('[data-gsb-vessel-empty]'),
      vesselBox: root.querySelector('[data-gsb-vessel-box]'),
      lining: root.querySelector('[data-gsb-lining]'),
      seal: root.querySelector('[data-gsb-seal]'),
      vesselCap: root.querySelector('[data-gsb-vessel-cap]'),
      boxline: root.querySelector('[data-gsb-boxline]'),
      starter: root.querySelector('[data-gsb-starter]'),
      starterBtn: root.querySelector('[data-gsb-starter-btn]'),
      addons: root.querySelector('[data-gsb-addons]'),
      addonCart: root.querySelector('[data-gsb-addon-cart]'),
      addonCartList: root.querySelector('[data-gsb-addon-cart-list]'),
      boxlist: root.querySelector('[data-gsb-boxlist]'),
      boxfoot: root.querySelector('[data-gsb-boxfoot]'),
      pieceCount: root.querySelector('[data-gsb-piececount]'),
      footCount: root.querySelector('[data-gsb-foot-count]'),
      subtotal: root.querySelector('[data-gsb-subtotal]'),
      review: root.querySelector('[data-gsb-review]'),
      minhint: root.querySelector('[data-gsb-minhint]'),
      boxcol: root.querySelector('[data-gsb-boxcol]'),
      sheetClose: root.querySelector('[data-gsb-sheet-close]'),
      scrim: root.querySelector('[data-gsb-scrim]'),
      bar: root.querySelector('[data-gsb-bar]'),
      barOpen: root.querySelector('[data-gsb-bar-open]'),
      barPieces: root.querySelector('[data-gsb-bar-pieces]'),
      barSub: root.querySelector('[data-gsb-bar-sub]'),
      reviewMobile: root.querySelector('[data-gsb-review-mobile]'),
      reviewEmpty: root.querySelector('[data-gsb-review-empty]'),
      reviewVesselBox: root.querySelector('[data-gsb-review-vessel-box]'),
      reviewLining: root.querySelector('[data-gsb-review-lining]'),
      reviewCardTo: root.querySelector('[data-gsb-review-card-to]'),
      reviewCardMsg: root.querySelector('[data-gsb-review-card-msg]'),
      reviewCardFrom: root.querySelector('[data-gsb-review-card-from]'),
      reviewItems: root.querySelector('[data-gsb-review-items]'),
      reviewCount: root.querySelector('[data-gsb-review-count]'),
      reviewSubtotal: root.querySelector('[data-gsb-review-subtotal]'),
      reviewTotal: root.querySelector('[data-gsb-review-total]'),
      addcartError: root.querySelector('[data-gsb-addcart-error]'),
      addcartErrorText: root.querySelector('[data-gsb-addcart-error-text]'),
      kitmodalScrim: root.querySelector('[data-gsb-kitmodal-scrim]'),
      kitmodal: root.querySelector('[data-gsb-kitmodal]'),
      kitmodalClose: root.querySelector('[data-gsb-kitmodal-close]'),
      kitmodalImg: root.querySelector('[data-gsb-kitmodal-img]'),
      kitmodalName: root.querySelector('[data-gsb-kitmodal-name]'),
      kitmodalBlurb: root.querySelector('[data-gsb-kitmodal-blurb]'),
      kitmodalIncl: root.querySelector('[data-gsb-kitmodal-incl]'),
      kitmodalInclList: root.querySelector('[data-gsb-kitmodal-incl-list]'),
      kitmodalPrice: root.querySelector('[data-gsb-kitmodal-price]'),
      kitmodalAdd: root.querySelector('[data-gsb-kitmodal-add]'),
      kitmodalError: root.querySelector('[data-gsb-kitmodal-error]')
    };
    this.cardById = {};
    this.cards.forEach(function (c) { this.cardById[c.getAttribute('data-gsb-id')] = c; }, this);

    this.assignFeelings();
    this.renderOccasions();
    this.renderMood();
    this.renderTypes();
    this.updateKitXsell();
    this.applyFilters();
    this.bind();
    this.setupScrollCues();
    this.restore();
  }

  /* ——— selection helpers ——— */
  Builder.prototype.total = function () { return this.boxArr.reduce(function (s, e) { return s + e.qty; }, 0); };
  Builder.prototype.entry = function (id) { id = String(id); for (var i = 0; i < this.boxArr.length; i++) if (this.boxArr[i].id === id) return this.boxArr[i]; return null; };
  Builder.prototype.pieces = function () { var self = this, out = []; this.boxArr.forEach(function (e) { var p = self.byId[e.id]; if (!p) return; for (var i = 0; i < e.qty; i++) out.push(p); }); return out; };
  Builder.prototype.subtotalCents = function () { var self = this; return this.boxArr.reduce(function (s, e) { var p = self.byId[e.id]; return s + ((p && p.price || 0) * e.qty); }, 0); };

  /* ——— feeling membership ——— */
  Builder.prototype.feelingsFor = function (card) {
    var cardMoods = tokens(card.getAttribute('data-gsb-mood')), ids = [];
    this.feelings.forEach(function (f) { var fm = tokens(f.moods); if (fm.some(function (m) { return cardMoods.indexOf(m) !== -1; })) ids.push(f.id); });
    return ids;
  };
  Builder.prototype.assignFeelings = function () { var self = this; this.cards.forEach(function (c) { c._feelings = self.feelingsFor(c); }); };
  Builder.prototype.inScope = function (card) { return !this.activeFeeling || card._feelings.indexOf(this.activeFeeling) !== -1; };
  Builder.prototype.typeMatches = function (card, t) {
    if (!t || t.id === 'all') return true;
    var ctype = (card.getAttribute('data-gsb-type') || '').toLowerCase();
    var ctags = (card.getAttribute('data-gsb-tags') || '').toLowerCase();
    var hay = ' ' + ctype + ' ';
    return (t.match || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean).some(function (e) {
      return e && (ctype === e || hay.indexOf(' ' + e + ' ') !== -1 || ctags.indexOf('|' + e + '|') !== -1);
    });
  };

  /* ——— filter chips ——— */
  Builder.prototype.renderOccasions = function () {
    var self = this, row = this.els.occasionrow;
    if (!row) return;
    if (!this.occasions.length) { if (this.els.occasionWrap) this.els.occasionWrap.hidden = true; return; }
    if (this.els.occasionWrap) this.els.occasionWrap.hidden = false;
    clear(row);
    // Single-select with an explicit "Any feeling" anchor, mirroring the Type row's
    // "Everything" chip: activeOccasion === null means the anchor is lit. Picking a
    // feeling sets it; picking the anchor resets. No hidden none-selected state.
    var mk = function (id, label) {
      var b = el('button', 'gsb__ochip mds-focusable', label); b.type = 'button';
      b.setAttribute('data-gsb-occasion', id);
      var on = (id === 'all') ? (self.activeOccasion === null) : (self.activeOccasion === id);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) b.classList.add('on');
      b.addEventListener('click', function () {
        self.activeOccasion = (id === 'all') ? null : id;
        if (id === 'all' && self.els.occasionrow.parentNode) self.els.occasionrow.parentNode.scrollLeft = 0;
        self.renderOccasions(); self.applyFilters(); self.updateKitXsell(); self.persist();
      });
      return b;
    };
    row.appendChild(mk('all', this.cfg.occasion_all_label || 'Any feeling'));
    this.occasions.forEach(function (o) { row.appendChild(mk(o.id, o.label)); });
    this.syncScrollCues();
  };
  Builder.prototype.renderMood = function () {
    if (!this.els.moodrow) return;
    var self = this; clear(this.els.moodrow);
    this.feelings.forEach(function (f) {
      var b = el('button', 'gsb__mchip mds-focusable', f.label); b.type = 'button';
      b.setAttribute('data-gsb-mood', f.id);
      b.setAttribute('aria-pressed', self.activeFeeling === f.id ? 'true' : 'false');
      if (self.activeFeeling === f.id) b.classList.add('on');
      b.addEventListener('click', function () {
        self.activeFeeling = (self.activeFeeling === f.id) ? null : f.id;
        self.activeOccasion = null;   // a manual mood pick deselects the occasion preset
        self.renderOccasions(); self.renderMood(); self.applyFilters(); self.updateKitXsell();
      });
      self.els.moodrow.appendChild(b);
    });
  };
  Builder.prototype.renderTypes = function () {
    if (!this.els.chiprow) return;
    var self = this; clear(this.els.chiprow);
    var mk = function (id, label) {
      var b = el('button', 'gsb__tchip3 mds-focusable', label); b.type = 'button';
      b.setAttribute('data-gsb-chip', id);
      b.setAttribute('aria-pressed', id === self.activeType ? 'true' : 'false');
      if (id === self.activeType) b.classList.add('on');
      b.addEventListener('click', function () { self.activeType = id; if (id === 'all' && self.els.chiprow.parentNode) self.els.chiprow.parentNode.scrollLeft = 0; self.renderTypes(); self.applyFilters(); self.persist(); });
      return b;
    };
    this.els.chiprow.appendChild(mk('all', this.cfg.all_label || 'Everything'));
    this.types.forEach(function (t) { self.els.chiprow.appendChild(mk(t.id, t.label)); });
    this.syncScrollCues();
  };
  /* the right-edge fade/chevron on a filter row is hidden once its chips are
     scrolled to the end (or don't overflow), so it never falsely implies "more" */
  Builder.prototype.syncScrollCues = function () {
    var vps = this.root.querySelectorAll('[data-gsb-scroller]');
    Array.prototype.forEach.call(vps, function (vp) {
      var row = vp.parentNode; if (!row) return;
      var atEnd = vp.scrollLeft + vp.clientWidth >= vp.scrollWidth - 2;
      row.classList.toggle('gsb__frow--end', atEnd);
    });
  };
  Builder.prototype.setupScrollCues = function () {
    var self = this, vps = this.root.querySelectorAll('[data-gsb-scroller]');
    Array.prototype.forEach.call(vps, function (vp) {
      vp.addEventListener('scroll', function () { self.syncScrollCues(); }, { passive: true });
    });
    // one resize listener per instance (guards against accumulation on theme-editor reloads)
    if (!this._resizeCue) {
      this._resizeCue = function () {
        // if the sheet is open and we've left mobile, close it so the scroll-lock/inert don't get stuck
        if (!self.isSheetMode() && self.els.boxcol && self.els.boxcol.classList.contains('open')) self.closeSheet();
        self.syncScrollCues();
      };
      window.addEventListener('resize', this._resizeCue);
    }
    // defer the first measurement to after layout — scrollWidth/clientWidth read 0
    // during synchronous init, which would wrongly hide the cue on landing
    var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
    raf(function () { self.syncScrollCues(); });
  };

  /* ——— ready-made kit cross-sell + popover ——— */
  Builder.prototype.updateKitXsell = function () {
    var x = this.els.kitXsell; if (!x) return;
    var occ = this.activeOccasion ? this.occasionsById[this.activeOccasion] : null;
    var kit = occ && occ.kit ? occ.kit : null;
    this.activeKit = kit;
    if (!kit) { x.hidden = true; return; }
    x.hidden = false;
    if (this.els.kitName) this.els.kitName.textContent = kit.title || '';
  };
  Builder.prototype.openKitModal = function () {
    var kit = this.activeKit, e = this.els; if (!kit || !e.kitmodalScrim) return;
    if (e.kitmodalName) e.kitmodalName.textContent = kit.title || '';
    if (e.kitmodalBlurb) e.kitmodalBlurb.textContent = kit.blurb || '';
    if (e.kitmodalPrice) e.kitmodalPrice.textContent = kit.price || '';
    if (e.kitmodalImg) {
      var prev = e.kitmodalImg.querySelector('img'); if (prev) prev.parentNode.removeChild(prev);
      if (kit.image) { var im = document.createElement('img'); im.src = kit.image; im.alt = ''; im.loading = 'lazy'; e.kitmodalImg.insertBefore(im, e.kitmodalImg.firstChild); }
    }
    if (e.kitmodalIncl && e.kitmodalInclList) {
      clear(e.kitmodalInclList);
      var items = Array.isArray(kit.includes) ? kit.includes.filter(Boolean) : [];
      if (items.length) {
        e.kitmodalIncl.hidden = false;
        items.forEach(function (it) { var li = el('li', null); li.appendChild(svg(IC.leaf, 14)); li.appendChild(document.createTextNode(' ' + it)); e.kitmodalInclList.appendChild(li); });
      } else { e.kitmodalIncl.hidden = true; }
    }
    if (e.kitmodalError) e.kitmodalError.classList.remove('show');
    if (e.kitmodalAdd) e.kitmodalAdd.disabled = false;
    this._kitOpener = (document.activeElement && document.activeElement !== document.body) ? document.activeElement : e.kitView;
    e.kitmodalScrim.classList.add('open');
    this.bgInert(true);
    if (e.kitmodal) { this._kitTrap = this._kitTrap || this.makeTrap(e.kitmodal); e.kitmodal.addEventListener('keydown', this._kitTrap); }
    if (e.kitmodalClose) try { e.kitmodalClose.focus(); } catch (er) { /* noop */ }
  };
  /* make the builder background inert while the kit popover is open */
  Builder.prototype.bgInert = function (on) {
    var self = this;
    ['.gsb__stepind', '.gsb__stepbody'].forEach(function (sel) { self.setInert(sel, on); });
  };
  Builder.prototype.closeKitModal = function () {
    var e = this.els; if (!e.kitmodalScrim || !e.kitmodalScrim.classList.contains('open')) return;
    e.kitmodalScrim.classList.remove('open');
    this.bgInert(false);
    if (this._kitTrap && e.kitmodal) e.kitmodal.removeEventListener('keydown', this._kitTrap);
    var opener = this._kitOpener || e.kitView;
    if (opener) try { opener.focus(); } catch (er) { /* noop */ }
  };
  /* Native cart drawer (2026-08-24): terminal add steps open the drawer in
     place instead of navigating to /cart — the builder overlays close first
     so two scroll-locked layers never stack. Falls back to the /cart
     navigation when the drawer section is absent (no-JS-drawer safety). */
  Builder.prototype.finishToCart = function () {
    if (document.querySelector('[data-section-type="cart-drawer"]')) {
      try { if (typeof this.closeKitModal === 'function') this.closeKitModal(); } catch (e) { }
      try { if (typeof this.closeSheet === 'function') this.closeSheet(); } catch (e) { }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    } else {
      window.location.href = '/cart';
    }
  };
  Builder.prototype.addKitToCart = function () {
    var kit = this.activeKit, self = this; if (!kit) return;
    if (this.els.kitmodalError) this.els.kitmodalError.classList.remove('show');
    if (!kit.variant_id) { if (kit.url) window.location.href = kit.url; return; }
    var btn = this.els.kitmodalAdd; if (btn) btn.disabled = true;
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [{ id: kit.variant_id, quantity: 1 }] }) })
      .then(function (res) { if (!res.ok) return res.json().catch(function () { return {}; }).then(function (d) { throw new Error((d && d.description) || 'That kit couldn’t be added — try once more.'); }); return res.json(); })
      .then(function () { gsbTrack('gift_builder_kit_add', { kit_variant_id: kit.variant_id, occasion: (self.activeOccasion || '') }); self.finishToCart(); })
      .catch(function (err) {
        if (btn) btn.disabled = false;
        if (self.els.kitmodalError) { self.els.kitmodalError.textContent = err.message || 'That kit couldn’t be added — try once more.'; self.els.kitmodalError.classList.add('show'); }
      });
  };

  /* seed the box with the configured starter trio (handles); top up any
     slots that didn't resolve (typo / excluded / out-of-stock) from most-gifted */
  Builder.prototype.seedTrio = function () {
    var self = this, target = Math.max(this.min, 3), seeded = 0;
    var handles = String(this.cfg.starter_handles || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
    handles.forEach(function (h) {
      var card = self.cards.filter(function (c) { return (c.getAttribute('data-gsb-handle') || '').toLowerCase() === h; })[0];
      if (card && !self.entry(card.getAttribute('data-gsb-id'))) { self.addById(card.getAttribute('data-gsb-id')); seeded += 1; }
    });
    if (seeded < target) {
      var gifted = this.cards.filter(function (c) { return c.hasAttribute('data-gsb-gifted'); });
      for (var i = 0; i < gifted.length && seeded < target; i++) {
        var id = gifted[i].getAttribute('data-gsb-id');
        if (!self.entry(id)) { self.addById(id); seeded += 1; }
      }
    }
    this.renderBox(); this.applyFilters(); this.persist();
  };

  /* ——— add-ons: too large for the box, so they go to the GENERAL cart ——— */
  Builder.prototype.bindAddons = function () {
    var self = this;
    this.addonCards = this.els.addons ? Array.prototype.slice.call(this.els.addons.querySelectorAll('[data-gsb-addon-card]')) : [];
    if (!this.addonCards.length) { if (this.els.addons) this.els.addons.hidden = true; return; }
    this.els.addons.hidden = false;
    this.addonCards.forEach(function (card) {
      var btn = card.querySelector('[data-gsb-addon-add]');
      if (btn) btn.addEventListener('click', function () { self.toggleAddon(card, btn); });
    });
    this.refreshAddonState();
  };
  Builder.prototype.setAddonCard = function (card, inCart) {
    card.classList.toggle('in', !!inCart);
    var btn = card.querySelector('[data-gsb-addon-add]'), lbl = card.querySelector('[data-gsb-addon-label]');
    if (btn) btn.setAttribute('aria-pressed', inCart ? 'true' : 'false');
    if (lbl) lbl.textContent = inCart ? (this.cfg.addon_added_label || 'Added — remove') : (this.cfg.addon_add_label || 'Add to order');
  };
  var ADDON_HDRS = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  // resolve the STANDALONE add-on line key for a variant (a line with NO _bundle_id),
  // so a remove can never touch a bundled box line that happens to share the variant id
  Builder.prototype.addonLineKey = function (vid) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        var line = cart.items.filter(function (i) {
          return String(i.variant_id) === String(vid) && !(i.properties && i.properties._bundle_id);
        })[0];
        return line ? line.key : null;
      });
  };
  Builder.prototype.toggleAddon = function (card, btn) {
    var self = this, vid = card.getAttribute('data-gsb-variant'), inCart = card.classList.contains('in');
    if (!vid) return;
    if (btn) btn.disabled = true;
    this._addonSeq = (this._addonSeq || 0) + 1;  // any mutation invalidates an in-flight preflight
    var p = inCart
      ? self.addonLineKey(vid).then(function (key) {
          if (!key) return; // already gone — treat as removed
          return fetch('/cart/change.js', { method: 'POST', headers: ADDON_HDRS, body: JSON.stringify({ id: key, quantity: 0 }) })
            .then(function (r) { if (!r.ok) throw 0; });
        })
      : fetch('/cart/add.js', { method: 'POST', headers: ADDON_HDRS, body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] }) })
          .then(function (r) { if (!r.ok) throw 0; });
    p.then(function () { self.setAddonCard(card, !inCart); self.renderAddonCart(); })
     .catch(function () {
       // surface the failure (do not swallow) — the buyer can retry
       if (self.els.announce) self.els.announce.textContent = "Couldn't update your order — please try again.";
       card.classList.add('gsb__addon--err');
       setTimeout(function () { card.classList.remove('gsb__addon--err'); }, 2400);
     })
     .then(function () { if (btn) btn.disabled = false; });
  };
  Builder.prototype.refreshAddonState = function () {
    var self = this;
    if (!this.addonCards || !this.addonCards.length) return;
    var seq = this._addonSeq || 0;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if ((self._addonSeq || 0) !== seq) return; // a user toggle landed mid-flight — don't clobber it
        var inCart = {};
        if (cart && cart.items) cart.items.forEach(function (i) { inCart[String(i.variant_id)] = true; });
        self.addonCards.forEach(function (card) { self.setAddonCard(card, !!inCart[card.getAttribute('data-gsb-variant')]); });
        self.renderAddonCart();
      })
      .catch(function () {});
  };
  Builder.prototype.renderAddonCart = function () {
    var self = this, wrap = this.els.addonCart, list = this.els.addonCartList;
    if (!wrap || !list) return;
    var inCards = (this.addonCards || []).filter(function (c) { return c.classList.contains('in'); });
    clear(list);
    if (!inCards.length) { wrap.hidden = true; return; }
    wrap.hidden = false;
    inCards.forEach(function (card) {
      var row = el('div', 'gsb__addon-cart-row');
      row.appendChild(el('span', 'gsb__addon-cart-name', card.getAttribute('data-gsb-title') || ''));
      row.appendChild(el('span', 'gsb__addon-cart-price', self.fmt(parseInt(card.getAttribute('data-gsb-price'), 10) || 0)));
      var x = el('button', 'gsb__addon-cart-x', '×'); x.type = 'button';
      x.setAttribute('aria-label', 'Remove ' + (card.getAttribute('data-gsb-title') || '') + ' from your order');
      x.addEventListener('click', function () { self.toggleAddon(card, card.querySelector('[data-gsb-addon-add]')); });
      row.appendChild(x);
      list.appendChild(row);
    });
  };

  /* ——— filtering ——— */
  // "For" (occasion) filters the grid directly: a card shows if ANY of the
  // occasion's filter_tags is present in the card's tag bag (OR-matched).
  Builder.prototype.occasionMatches = function (card) {
    if (!this.activeOccasion) return true;
    var occ = this.occasionsById[this.activeOccasion];
    if (!occ || !occ._tags || !occ._tags.length) return true;
    var ctags = (card.getAttribute('data-gsb-tags') || '').toLowerCase();
    for (var i = 0; i < occ._tags.length; i++) { if (ctags.indexOf('|' + occ._tags[i] + '|') !== -1) return true; }
    return false;
  };
  Builder.prototype.cardMatches = function (card) {
    if (!this.occasionMatches(card)) return false;
    if (this.activeType !== 'all' && !this.typeMatches(card, this.typesById[this.activeType])) return false;
    return true;
  };
  Builder.prototype.applyFilters = function () {
    var self = this, shown = 0;
    this.cards.forEach(function (card) {
      // gallery shows ONLY products matching the active filter. Box selections are
      // preserved in the box panel — they no longer bleed into every filter view.
      var visible = self.cardMatches(card);
      if (visible) { card.removeAttribute('data-gsb-hidden'); shown += 1; } else card.setAttribute('data-gsb-hidden', 'true');
    });
    if (this.els.status) this.els.status.textContent = shown === 1 ? '1 piece' : shown + ' pieces';
    var any = this.cards.some(function (c) { return self.cardMatches(c); });
    if (this.els.zero) this.els.zero.hidden = any;
  };

  /* ——— steps + nav ——— */
  Builder.prototype.goStep = function (name) {
    var idx = STEPS.indexOf(name); if (idx < 0) return;
    if (idx > this.reached) this.reached = idx;
    gsbTrack('gift_builder_step', { from_step: this.step || 'build', to_step: name });
    this.step = name;
    /* always rebuild Review from current state so it can't go stale, regardless of
       entry path (CTA, the step chip, or back-then-forward) */
    if (name === 'review') { this.captureGift(); this.renderReview(); }
    var sections = this.root.querySelectorAll('[data-gsb-step]');
    Array.prototype.forEach.call(sections, function (s) { s.hidden = s.getAttribute('data-gsb-step') !== name; });
    var reached = this.reached;
    this.els.segs.forEach(function (tab) {
      var di = STEPS.indexOf(tab.getAttribute('data-gsb-seg'));
      tab.classList.remove('current', 'done', 'upcoming');
      if (di === idx) { tab.classList.add('current'); tab.setAttribute('aria-selected', 'true'); tab.removeAttribute('disabled'); }
      else if (di <= reached) { tab.classList.add('done'); tab.setAttribute('aria-selected', 'false'); tab.removeAttribute('disabled'); }
      else { tab.classList.add('upcoming'); tab.setAttribute('aria-selected', 'false'); tab.setAttribute('disabled', 'disabled'); }
    });
    if (this.els.topback && this.els.backLabel) this.els.backLabel.textContent = idx === 0 ? (this.cfg.back_shop_label || 'Back to shop') : 'Back';
    if (name !== 'build') this.closeSheet();
    var rmScroll = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { this.root.scrollIntoView({ behavior: rmScroll ? 'auto' : 'smooth', block: 'start' }); } catch (e) { /* noop */ }
    var activeEl = this.root.querySelector('[data-gsb-step="' + name + '"]');
    if (activeEl) {
      // focus the step's OWN heading for SR flow — skip the supplementary add-on
      // section's heading (it's not the build step's title)
      var hs = activeEl.querySelectorAll('h1, h2, h3'), h = null;
      for (var hi = 0; hi < hs.length; hi++) { if (!hs[hi].closest('.gsb__addons')) { h = hs[hi]; break; } }
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e2) { try { h.focus(); } catch (e3) { /* noop */ } } }
    }
  };
  Builder.prototype.goNote = function () { if (this.total() >= this.min) this.goStep('note'); };
  Builder.prototype.goReview = function () { this.goStep('review'); };
  Builder.prototype.nudgeMin = function () {
    if (this.isSheetMode()) this.openSheet();
    if (this.els.review) try { this.els.review.focus(); } catch (e) { /* noop */ }
  };

  /* ——— bind ——— */
  Builder.prototype.bind = function () {
    var self = this;
    this.cards.forEach(function (card) {
      var add = card.querySelector('[data-gsb-add]');
      if (add) add.addEventListener('click', function () { self.add(card.getAttribute('data-gsb-id')); });
    });
    if (this.els.topback) this.els.topback.addEventListener('click', function (e) {
      var idx = STEPS.indexOf(self.step);
      if (idx > 0) { e.preventDefault(); self.goStep(STEPS[idx - 1]); }
    });
    this.els.segs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var di = STEPS.indexOf(tab.getAttribute('data-gsb-seg'));
        if (di < 0 || di > self.reached) return;
        if (di > 0 && self.total() < self.min) { self.nudgeMin(); return; }  // can't proceed below the minimum
        self.goStep(STEPS[di]);
      });
    });
    if (this.els.review) this.els.review.addEventListener('click', function () { self.goNote(); });
    if (this.els.reviewMobile) this.els.reviewMobile.addEventListener('click', function () { self.goNote(); });
    if (this.els.starterBtn) this.els.starterBtn.addEventListener('click', function () { self.seedTrio(); });
    this.bindAddons();
    if (this.els.zeroRelax) this.els.zeroRelax.addEventListener('click', function () { self.activeFeeling = null; self.activeOccasion = null; self.activeType = 'all'; self.renderOccasions(); self.renderMood(); self.renderTypes(); self.applyFilters(); self.updateKitXsell(); Array.prototype.forEach.call(self.root.querySelectorAll('[data-gsb-scroller]'), function (vp) { vp.scrollLeft = 0; }); self.syncScrollCues(); self.persist(); });
    // kit cross-sell + modal
    if (this.els.kitView) this.els.kitView.addEventListener('click', function () { self.openKitModal(); });
    if (this.els.kitmodalClose) this.els.kitmodalClose.addEventListener('click', function () { self.closeKitModal(); });
    if (this.els.kitmodalScrim) this.els.kitmodalScrim.addEventListener('click', function (e) { if (e.target === self.els.kitmodalScrim) self.closeKitModal(); });
    if (this.els.kitmodalAdd) this.els.kitmodalAdd.addEventListener('click', function () { self.addKitToCart(); });
    // mobile sheet
    if (this.els.barOpen) this.els.barOpen.addEventListener('click', function () { self.openSheet(); });
    if (this.els.sheetClose) this.els.sheetClose.addEventListener('click', function () { self.closeSheet(); });
    if (this.els.scrim) this.els.scrim.addEventListener('click', function () { self.closeSheet(); });
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.els.kitmodalScrim && self.els.kitmodalScrim.classList.contains('open')) { self.closeKitModal(); return; }
      if (self.els.boxcol && self.els.boxcol.classList.contains('open')) self.closeSheet();
    });
    // note step
    var noteContinue = this.root.querySelector('[data-gsb-note-continue]');
    var noteSkip = this.root.querySelector('[data-gsb-skip]');
    if (noteContinue) noteContinue.addEventListener('click', function () { self.captureGift(); self.goReview(); });
    if (noteSkip) noteSkip.addEventListener('click', function () { self.gift = null; self.goReview(); });
    this.bindGiftPreview();
    // review step
    var edit = this.root.querySelector('[data-gsb-edit]');
    var addcart = this.root.querySelector('[data-gsb-addcart]');
    if (edit) edit.addEventListener('click', function () { self.goStep('build'); });
    if (addcart) addcart.addEventListener('click', function () { self.addToCart(addcart); });
  };

  /* ——— mobile sheet ——— */
  Builder.prototype.isSheetMode = function () { return !!(window.matchMedia && window.matchMedia(SHEET_BP).matches); };
  Builder.prototype.makeTrap = function (box) {
    return function (e) {
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(box.querySelectorAll('button:not([disabled]), a[href], input, textarea, [tabindex]:not([tabindex="-1"])'), function (n) { return n.offsetParent !== null; });
      if (!f.length) return; var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
  };
  /* lock background scroll while a bottom sheet is open (iOS-safe: fix the body
     at the current offset so the page can't rubber-band behind the scrim) */
  Builder.prototype.lockScroll = function (on) {
    var b = document.body;
    if (on) {
      if (this._scrollLocked) return;              // idempotent — don't re-capture scrollY
      this._scrollLocked = true;
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      b.style.position = 'fixed'; b.style.top = (-this._scrollY) + 'px'; b.style.left = '0'; b.style.right = '0'; b.style.width = '100%';
    } else {
      if (!this._scrollLocked) return;             // never locked — don't stomp host body styles
      this._scrollLocked = false;
      b.style.position = ''; b.style.top = ''; b.style.left = ''; b.style.right = ''; b.style.width = '';
      if (this._scrollY != null) { window.scrollTo(0, this._scrollY); this._scrollY = null; }
    }
  };
  /* reference-counted inert so an element shared by two overlays (e.g. .gsb__stepind,
     used by both the sheet and the kit modal) stays inert until all of them release it */
  Builder.prototype.setInert = function (sel, on) {
    var n = this.root.querySelector(sel); if (!n) return;
    this._inertCounts = this._inertCounts || {};
    var c = (this._inertCounts[sel] || 0) + (on ? 1 : -1); if (c < 0) c = 0;
    this._inertCounts[sel] = c;
    if (c > 0) { n.setAttribute('aria-hidden', 'true'); try { n.inert = true; } catch (e) { /* noop */ } }
    else { n.removeAttribute('aria-hidden'); try { n.inert = false; } catch (e) { /* noop */ } }
  };
  /* make the build-step background inert while the sheet is open (the sheet itself
     lives in .gsb__fill3-right, so inert only the siblings behind it) */
  Builder.prototype.sheetInert = function (on) {
    var self = this;
    ['.gsb__stepind', '.gsb__fill3-left'].forEach(function (sel) { self.setInert(sel, on); });
  };
  Builder.prototype.openSheet = function () {
    var box = this.els.boxcol; if (!box || !this.isSheetMode()) return;
    if (box.classList.contains('open')) return;    // idempotent — avoid double lock/inert on fast re-tap
    box.classList.add('open'); box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true');
    if (this.els.scrim) { this.els.scrim.hidden = false; this.els.scrim.classList.add('open'); }
    this.lockScroll(true); this.sheetInert(true);
    // hide the sticky bar while the sheet is open so it can't be reached behind the
    // scrim (the sheet's focus trap would otherwise let Shift-Tab escape into it)
    if (this.els.bar) this.els.bar.hidden = true;
    if (this.els.barOpen) this.els.barOpen.setAttribute('aria-expanded', 'true');
    this._trap = this._trap || this.makeTrap(box); box.addEventListener('keydown', this._trap);
    if (this.els.sheetClose) try { this.els.sheetClose.focus(); } catch (e) { /* noop */ }
  };
  Builder.prototype.closeSheet = function () {
    var box = this.els.boxcol; if (!box || !box.classList.contains('open')) return;
    box.classList.remove('open'); box.removeAttribute('role'); box.removeAttribute('aria-modal');
    if (this._trap) box.removeEventListener('keydown', this._trap);
    if (this.els.scrim) { this.els.scrim.classList.remove('open'); this.els.scrim.hidden = true; }
    this.lockScroll(false); this.sheetInert(false);
    // restore the bar only on the build step (goStep hides it on note/review)
    if (this.els.bar) this.els.bar.hidden = (this.step && this.step !== 'build');
    if (this.els.barOpen) this.els.barOpen.setAttribute('aria-expanded', 'false');
    if (this.els.barOpen) try { this.els.barOpen.focus(); } catch (e) { /* noop */ }
  };

  /* ——— mutations ——— */
  Builder.prototype.add = function (id) {
    if (this._undoEl) this.clearUndo();
    id = String(id); var card = this.cardById[id];
    var e = this.entry(id);
    if (this.total() >= this.max && !e) { return; }
    if (e) { if (e.qty >= this.maxPerItem || this.total() >= this.max) return; e.qty += 1; }
    else { this.boxArr.push({ id: id, qty: 1 }); if (card) this.flyToBox(card); if (this.isSheetMode()) this.pulseBar(); }
    this.renderBox(); this.persist();
  };
  Builder.prototype.inc = function (id) { var e = this.entry(id); if (!e || e.qty >= this.maxPerItem || this.total() >= this.max) return; e.qty += 1; this.renderBox(); this.persist(); };
  Builder.prototype.dec = function (id) { var e = this.entry(id); if (!e) return; e.qty -= 1; if (e.qty <= 0) { this.removeEntry(id); return; } this.renderBox(); this.persist(); };
  Builder.prototype.removeEntry = function (id) {
    id = String(id); var ex = this.entry(id); var removed = ex ? { id: ex.id, qty: ex.qty } : null;
    this.boxArr = this.boxArr.filter(function (e) { return e.id !== id; });
    this.renderBox(); this.applyFilters(); this.persist();
    if (removed) this.showUndo(removed);
  };
  Builder.prototype.clearUndo = function () {
    if (this._undoTimer) { clearTimeout(this._undoTimer); this._undoTimer = null; }
    if (this._undoEl && this._undoEl.parentNode) this._undoEl.parentNode.removeChild(this._undoEl);
    this._undoEl = null;
  };
  Builder.prototype.showUndo = function (removed) {
    var self = this, e = this.els; if (!e.boxlist || !e.boxlist.parentNode) return;
    this.clearUndo();
    var p = this.byId[removed.id];
    var box = el('div', 'gsb__undo');
    box.appendChild(el('span', null, 'Removed ' + (p ? '“' + p.title + '”' : 'an item')));
    var btn = el('button', 'gsb__undo-btn', 'Undo'); btn.type = 'button';
    btn.addEventListener('click', function () {
      var q = Math.min(removed.qty, self.maxPerItem);
      if (!self.entry(removed.id) && self.total() + q <= self.max) self.boxArr.push({ id: removed.id, qty: q });
      self.clearUndo(); self.renderBox(); self.applyFilters(); self.persist();
    });
    box.appendChild(btn);
    e.boxlist.parentNode.insertBefore(box, e.boxlist);
    this._undoEl = box;
    this._undoTimer = setTimeout(function () { self.clearUndo(); }, 6000);
  };

  /* ——— fly-to-box ——— */
  /* mobile add-confirmation: the fly-to-box can't run on mobile (the box lining is
     in the closed sheet), so pulse the sticky bar to confirm the piece landed */
  Builder.prototype.pulseBar = function () {
    var bar = this.els.bar; if (!bar) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    bar.classList.remove('gsb__mbar3--pulse'); void bar.offsetWidth; bar.classList.add('gsb__mbar3--pulse');
  };
  Builder.prototype.flyToBox = function (card) {
    var lining = this.els.lining, img = card.querySelector('.gsb__pc3-imgel');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!lining || !img || reduce) return;
    var l = lining.getBoundingClientRect(); if (!l.width || lining.offsetParent === null) return;
    var s = card.getBoundingClientRect();
    var fly = el('div', 'gsb__fly'); var clone = document.createElement('img'); clone.src = img.currentSrc || img.src; fly.appendChild(clone);
    fly.style.cssText = 'position:fixed;z-index:200;pointer-events:none;left:' + (s.left + s.width / 2) + 'px;top:' + (s.top + s.height / 2) + 'px;width:' + s.width + 'px;transform:translate(-50%,-50%);transition:left .5s cubic-bezier(.16,1,.3,1),top .5s,width .5s,opacity .5s';
    var im2 = fly.querySelector('img'); if (im2) im2.style.cssText = 'width:100%;height:auto;display:block;border-radius:2px';
    document.body.appendChild(fly);
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      fly.style.left = (l.left + l.width / 2) + 'px'; fly.style.top = (l.top + l.height / 2) + 'px'; fly.style.width = (l.width * 0.3) + 'px'; fly.style.opacity = '0.6';
    }); });
    setTimeout(function () { if (fly.parentNode) fly.parentNode.removeChild(fly); }, 520);
  };

  /* ——— vessel thumbnails ——— */
  Builder.prototype.fillLining = function (lining, pieces) {
    if (!lining) return;
    clear(lining);
    lining.setAttribute('data-n', String(Math.min(pieces.length, 8)));
    pieces.slice(0, 8).forEach(function (p, i) {
      var t = el('div', 'gsb__vthumb3'); t.style.animationDelay = (i * 55) + 'ms'; t.style.transform = 'rotate(' + THUMB_ROT[i % THUMB_ROT.length] + 'deg)';
      if (p.image) { var im = document.createElement('img'); im.src = p.image; im.alt = ''; im.loading = 'lazy'; t.appendChild(im); }
      lining.appendChild(t);
    });
  };

  Builder.prototype.renderBox = function () {
    var self = this, e = this.els;
    var pieces = this.pieces(), count = pieces.length, sub = this.subtotalCents();
    var empty = count === 0, giftable = count >= this.min;

    // vessel: empty photo vs dimensional box
    if (e.vesselEmpty) e.vesselEmpty.hidden = !empty;
    if (e.vesselBox) e.vesselBox.hidden = empty;
    if (e.vesselCap) e.vesselCap.hidden = !empty;
    if (e.seal) e.seal.hidden = !giftable;
    if (e.lining) { clear(e.lining); if (!empty) this.fillLining(e.lining, pieces); }
    if (e.boxline) e.boxline.textContent = boxLine(Math.min(count, this.max));

    // starter (empty) vs list/foot/cta (filled)
    if (e.starter) e.starter.hidden = !empty;
    if (e.boxfoot) e.boxfoot.hidden = empty;
    if (e.review) { e.review.hidden = empty; e.review.disabled = !giftable; }
    if (e.boxlist) {
      e.boxlist.hidden = empty;
      clear(e.boxlist);
      if (!empty) {
        var atMax = count >= this.max;
        this.boxArr.forEach(function (entry) {
          var p = self.byId[entry.id]; if (!p) return;
          var row = el('div', 'gsb__brow3');
          var thumb = el('span', 'gsb__brow3-thumb'); if (p.image) { var im = document.createElement('img'); im.src = p.image; im.alt = ''; im.loading = 'lazy'; thumb.appendChild(im); } row.appendChild(thumb);
          var info = el('span', 'gsb__brow3-info');
          info.appendChild(el('span', 'gsb__brow3-name', p.title));
          var price = el('span', 'gsb__brow3-price', self.fmt(p.price * entry.qty));
          if (entry.qty > 1) { var ea = el('span', null, ' · ' + self.fmt(p.price) + ' ea'); ea.style.opacity = '.6'; price.appendChild(ea); }
          info.appendChild(price); row.appendChild(info);
          var controls = el('span', 'gsb__brow3-controls');
          var stp = el('span', 'gsb__stepper3'); stp.setAttribute('role', 'group'); stp.setAttribute('aria-label', 'Quantity of ' + p.title);
          var dec = el('button', null); dec.type = 'button'; dec.setAttribute('aria-label', 'One fewer ' + p.title); dec.appendChild(svg(IC.minus, 13));
          var qn = el('span', 'qn', String(entry.qty));
          var inc = el('button', null); inc.type = 'button'; inc.setAttribute('aria-label', 'One more ' + p.title); inc.appendChild(svg(IC.plus, 13)); inc.disabled = atMax || entry.qty >= self.maxPerItem;
          dec.addEventListener('click', function () { self.dec(entry.id); });
          inc.addEventListener('click', function () { self.inc(entry.id); });
          stp.appendChild(dec); stp.appendChild(qn); stp.appendChild(inc); controls.appendChild(stp);
          var rm = el('button', 'gsb__brow3-x mds-focusable'); rm.type = 'button'; rm.setAttribute('aria-label', 'Remove ' + p.title); rm.appendChild(svg(IC.x, 15));
          rm.addEventListener('click', function () { self.removeEntry(entry.id); });
          controls.appendChild(rm); row.appendChild(controls);
          e.boxlist.appendChild(row);
        });
      }
    }

    if (e.pieceCount) e.pieceCount.textContent = count + ' of ' + this.max;
    if (e.footCount) e.footCount.textContent = count + ' of ' + this.max;
    if (e.subtotal) e.subtotal.textContent = this.fmt(sub);
    if (e.minhint) {
      if (!giftable) e.minhint.textContent = (this.min - count) + ' more ' + (this.min - count === 1 ? 'piece' : 'pieces') + ' to make it giftable';
      else if (count >= this.max) e.minhint.textContent = 'Box full — remove a piece to add another.';
      else e.minhint.textContent = '';
    }
    if (e.announce) {
      var amsg = count + ' of ' + this.max + ' pieces';
      if (count >= this.max) amsg += ' — box full';
      else if (giftable) amsg += ' — giftable';
      e.announce.textContent = amsg;
    }

    // mobile bar — visible on the build step from landing (parity with the desktop
    // box aside). Empty state leads with the box identity + a tap-to-start instruction;
    // filled state switches to the piece count + subtotal.
    if (e.bar) e.bar.hidden = (this.step && this.step !== 'build');
    if (e.barPieces) e.barPieces.textContent = empty ? (this.cfg.box_label || 'Your box') : (count + ' of ' + this.max + ' pieces');
    if (e.barSub) {
      // empty → instruction; under the minimum → the gate (so the disabled CTA has a
      // visible reason); giftable → subtotal
      if (empty) e.barSub.textContent = (this.cfg.bar_empty_hint || 'Tap a piece above to start');
      else if (!giftable) { var need = this.min - count; e.barSub.textContent = need + ' more ' + (need === 1 ? 'piece' : 'pieces') + ' to continue'; }
      else e.barSub.textContent = this.fmt(sub) + ' · your box';
    }
    if (e.reviewMobile) e.reviewMobile.disabled = !giftable;

    // card states
    this.cards.forEach(function (card) {
      var entry = self.entry(card.getAttribute('data-gsb-id'));
      var qty = entry ? entry.qty : 0;
      card.classList.toggle('in', qty > 0);
      var add = card.querySelector('[data-gsb-add]'); if (add) { add.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false'); add.disabled = count >= self.max || qty >= self.maxPerItem; }
      var qtag = card.querySelector('[data-gsb-qtag]'); if (qtag) { if (qty > 0) { qtag.textContent = String(qty); qtag.hidden = false; } else qtag.hidden = true; }
    });
  };

  /* ——— gift note ——— */
  Builder.prototype.bindGiftPreview = function () {
    var r = this.root;
    var to = r.querySelector('[data-gsb-gift-to]'), from = r.querySelector('[data-gsb-gift-from]'), msg = r.querySelector('[data-gsb-gift-message]');
    var cTo = r.querySelector('[data-gsb-card-to]'), cFrom = r.querySelector('[data-gsb-card-from]'), cMsg = r.querySelector('[data-gsb-card-msg]'), cnt = r.querySelector('[data-gsb-gift-count]');
    var msgPh = (cMsg && cMsg.textContent) ? cMsg.textContent : 'Your message appears here, in our hand.';
    function sync() {
      if (cTo) cTo.textContent = 'To ' + ((to && to.value) ? to.value : 'their name') + ',';
      if (cFrom) cFrom.textContent = '— ' + ((from && from.value) ? from.value : 'you');
      if (cMsg && msg) {
        if (msg.value) { cMsg.textContent = msg.value; cMsg.classList.remove('ph-txt'); }
        else { cMsg.textContent = msgPh; cMsg.classList.add('ph-txt'); }
      }
      if (cnt && msg) cnt.textContent = String(msg.value.length);
    }
    [to, from, msg].forEach(function (f) { if (f) f.addEventListener('input', sync); });
    sync();
  };
  Builder.prototype.captureGift = function () {
    var r = this.root, to = r.querySelector('[data-gsb-gift-to]'), from = r.querySelector('[data-gsb-gift-from]'), msg = r.querySelector('[data-gsb-gift-message]');
    this.gift = { to: to ? to.value.trim() : '', from: from ? from.value.trim() : '', message: msg ? msg.value.trim() : '' };
  };

  /* ——— review ——— */
  Builder.prototype.renderReview = function () {
    var self = this, e = this.els;
    var pieces = this.pieces(), count = pieces.length, sub = this.subtotalCents();
    var empty = count === 0;
    if (e.reviewEmpty) e.reviewEmpty.hidden = !empty;
    if (e.reviewVesselBox) e.reviewVesselBox.hidden = empty;
    if (e.reviewLining) { clear(e.reviewLining); if (!empty) this.fillLining(e.reviewLining, pieces); }

    if (e.reviewItems) {
      clear(e.reviewItems);
      this.boxArr.forEach(function (entry) {
        var p = self.byId[entry.id]; if (!p) return;
        var item = el('div', 'gsb__rev3-item');
        var thumb = el('span', 'gsb__rev3-item-thumb'); if (p.image) { var im = document.createElement('img'); im.src = p.image; im.alt = ''; im.loading = 'lazy'; thumb.appendChild(im); } item.appendChild(thumb);
        var info = el('span', 'gsb__rev3-item-info');
        info.appendChild(el('span', 'gsb__rev3-item-name', p.title));
        info.appendChild(el('span', 'gsb__rev3-item-cat', (p.type || '') + (entry.qty > 1 ? ' · ×' + entry.qty : '')));
        item.appendChild(info);
        item.appendChild(el('span', 'gsb__rev3-item-price', self.fmt(p.price * entry.qty)));
        e.reviewItems.appendChild(item);
      });
    }
    if (e.reviewCount) e.reviewCount.textContent = count + (count === 1 ? ' piece' : ' pieces');
    if (e.reviewSubtotal) e.reviewSubtotal.textContent = this.fmt(sub);
    if (e.reviewTotal) e.reviewTotal.textContent = this.fmt(sub);

    var g = this.gift || {};
    if (e.reviewCardTo) e.reviewCardTo.textContent = 'To ' + (g.to || 'their name') + ',';
    if (e.reviewCardMsg) e.reviewCardMsg.textContent = g.message || (g.to || g.from ? '' : 'No note added — you can add one before checkout.');
    if (e.reviewCardFrom) e.reviewCardFrom.textContent = '— ' + (g.from || 'you');
  };

  /* ——— cart (verified contract — one line per product w/ real quantity) ——— */
  Builder.prototype.addToCart = function (btn) {
    var self = this;
    if (this.total() < this.min) return;
    if (btn) btn.disabled = true;
    if (this.els.addcartError) this.els.addcartError.classList.remove('show');
    if (!this.bundleId) this.bundleId = 'gsb-' + Math.floor(performance.now()).toString(36) + '-' + this.total();
    this.groupName = this.cfg.cart_group_name || 'Your Monsuri box';
    var bundleId = this.bundleId, groupName = this.groupName;

    function buildAddStep() {
      var items;
      try {
        items = self.boxArr.map(function (entry, idx) {
          var p = self.byId[entry.id];
          if (!p || !p.variant_id) throw new Error((p && p.title ? '"' + p.title + '"' : 'A piece') + ' is no longer available — remove it and try again.');
          var props = { _bundle_id: bundleId, _bundle_name: groupName };
          return { id: p.variant_id, quantity: entry.qty, properties: props };
        });
      } catch (g) { return Promise.reject(g); }
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: items }) })
        .then(function (res) { if (!res.ok) return res.json().catch(function () { return {}; }).then(function (d) { throw new Error((d && d.description) || 'A piece just sold out — swap it and try again.'); }); return res.json(); })
        .then(function () {
          self.bundleAdded = true;
          var g = self.gift || {};
          gsbTrack('gift_builder_add_to_cart_success', {
            item_count: self.total(),
            box_value: Math.round(self.subtotalCents()) / 100,
            has_note: !!(g.to || g.from || g.message),
            bundle_id: bundleId,
            currency: (self.cfg && self.cfg.currency) || 'USD'
          });
        });
    }
    var preflight = (this.attemptedAdd && !this.bundleAdded)
      ? fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { if (cart && cart.items && cart.items.some(function (i) { return i.properties && i.properties._bundle_id === bundleId; })) self.bundleAdded = true; })
          .catch(function () { })
      : Promise.resolve();
    preflight
      .then(function () { if (self.bundleAdded) return; self.attemptedAdd = true; self.persist(); return buildAddStep(); })
      .then(function () { return self.writeGiftNote(groupName); })
      .then(function () { self.clearPersist(); self.finishToCart(); })
      .catch(function (err) {
        if (self.bundleAdded) { self.clearPersist(); self.finishToCart(); return; }
        gsbTrack('gift_builder_add_to_cart_error', { error_message: (err && err.message ? String(err.message) : 'unknown').slice(0, 100), item_count: self.total() });
        if (btn) btn.disabled = false;
        if (self.els.addcartError) { self.els.addcartError.classList.add('show'); if (self.els.addcartErrorText) self.els.addcartErrorText.textContent = err.message || 'That did not go through — your box is saved, try once more.'; }
      });
  };
  /* The card goes ONLY to cart.note, laid out the way it is written: "To <name>," / the
     words / "— <from>" (the drawer and the Gift Finder read the name back out of the first
     line). Every order attribute lands in ShipStation's Note From Buyer as a <br/>-joined
     duplicate, so none is written — the retired keys are cleared (null) on the way. */
  Builder.prototype.writeGiftNote = function (groupName) {
    var attrs = { 'Gift Note \u2014 To': null, 'Gift Note \u2014 From': null, 'Gift Note': null, 'Gift Occasion': null, 'Your Monsuri box': null };
    attrs[groupName] = null;
    var body = { attributes: attrs };
    if (this.gift && (this.gift.to || this.gift.from || this.gift.message)) {
      var lines = [];
      /* ≤40: the length every "To" parser accepts (drawer, quiz, dashboard) */
      if (this.gift.to) lines.push('To ' + this.gift.to.slice(0, 40).trim() + ',');
      if (this.gift.message) lines.push(this.gift.message);
      if (this.gift.from) lines.push('\u2014 ' + this.gift.from);
      body.note = lines.join('\n');
    }
    var opts = { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) };
    function put() { return fetch('/cart/update.js', opts).then(function (res) { if (!res.ok) throw 0; return res; }); }
    /* retry once; a final failure is non-fatal — the box is already in the bag and the
       drawer's card field is open for the note */
    return put().catch(function () { return put(); }).catch(function () { /* non-fatal */ });
  };

  /* ——— persistence ——— */
  Builder.prototype.storageKey = function () { return STORAGE_PREFIX + (this.cfg.section_id || 'default'); };
  Builder.prototype.persist = function () { try { localStorage.setItem(this.storageKey(), JSON.stringify({ box: this.boxArr, occasion: this.activeOccasion, type: this.activeType, bundleId: this.bundleId || null, attemptedAdd: !!this.attemptedAdd, ts: Date.now() })); } catch (e) { /* noop */ } };
  Builder.prototype.clearPersist = function () { try { localStorage.removeItem(this.storageKey()); } catch (e) { /* noop */ } };
  Builder.prototype.addById = function (id, qty) {
    id = String(id); var card = this.cardById[id]; if (!card || !this.byId[id] || this.entry(id)) return;
    qty = Math.max(1, Math.min(qty || 1, this.maxPerItem)); if (this.total() + qty > this.max) qty = this.max - this.total(); if (qty <= 0) return;
    this.boxArr.push({ id: id, qty: qty });
  };
  Builder.prototype.restore = function () {
    var self = this, seeds = [], occasion = null, type = null;
    // A malformed %-sequence in the param makes decodeURIComponent throw; restore() runs inside the
    // constructor (initAll swallows a throw), which would silently disable the whole builder — so
    // degrade to "no seed" instead. Ids are only ever used as product-id lookup keys, never DOM'd.
    function safeDecode(s) { try { return decodeURIComponent(s); } catch (e) { return ''; } }
    var m = window.location.search.match(/[?&]gsb=([^&]+)/), seed = window.location.search.match(/[?&]seed=([^&]+)/);
    if (m) { safeDecode(m[1]).split(',').filter(Boolean).forEach(function (tok) { var p = tok.split(':'); seeds.push({ id: p[0], qty: parseInt(p[1], 10) || 1 }); }); }
    else if (seed) { var sv = safeDecode(seed[1]); if (sv) seeds.push({ id: sv, qty: 1 }); }
    else {
      try { var raw = localStorage.getItem(this.storageKey()); if (raw) { var d = JSON.parse(raw); if (d && d.ts && (Date.now() - d.ts) < 14 * 864e5 && Array.isArray(d.box)) { seeds = d.box; occasion = d.occasion || null; type = d.type || null; if (d.bundleId) self.bundleId = d.bundleId; if (d.attemptedAdd) self.attemptedAdd = true; } } } catch (e) { /* noop */ }
    }
    seeds.forEach(function (s) { self.addById(s.id, s.qty); });
    if (occasion && this.occasionsById[occasion]) { this.activeOccasion = occasion; this.renderOccasions(); this.updateKitXsell(); }
    if (type && (type === 'all' || this.typesById[type])) { this.activeType = type; this.renderTypes(); }
    this.applyFilters();
    this.renderBox();
    this.goStep('build');
  };

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-section-type="gift-set-builder"]'), function (root) {
      if (root.dataset.gsbInit === 'true') return;
      root.dataset.gsbInit = 'true';
      try { new Builder(root); } catch (e) { /* fail silently — server shell still renders */ }
    });
  }
  ready(initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
